import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../../../common/types';
import { Panel, PanelBody, PanelHead } from '../../../../components/primitives/Panel';
import { useGetPromoQuotaQuery } from '../../../../../../redux/api/fymAPI';
import type { CatalogItem, GetReleaseKitResponse, ReleasePlan } from '../../../../../../redux/api/fymAPI.types';
import { useQueryParam } from '../../../../../../utils/hooks/useQueryParam';
import { useIsDemoAccount } from '../../../../../accountSelector/useIsDemoAccount';
import { useMusic24Capabilities } from '../../../../hooks/useMusic24Capabilities';
import { useVideoCredits } from '../../../credits/useVideoCredits';
import { openPublishDialog } from '../../../publish/publishDialogStore';
import { downloadMediaUrl } from '../../../inlineMediaUrl';
import type { SerializedSocialChannel, SocialPlatform } from '../../../types';
import { addDays } from '../planMode';
import { planDayDate } from '../rolloutDayCopy';
import { ChannelsLine } from './ChannelsLine';
import { FreePlanLimits } from './FreePlanLimits';
import { LaunchQuestionsDialog } from './launch/LaunchQuestionsDialog';
import { LaunchSummary } from './launch/LaunchSummary';
import { ScheduleAllUpsell } from './launch/ScheduleAllUpsell';
import { NeedsYouPanel } from './NeedsYouPanel';
import { RolloutContext, useRollout, type RolloutContextValue } from './RolloutContext';
import {
  fitDrafts,
  planCounts,
  planDrafts,
  planNeeds,
  planSteps,
  type FitResult,
  type PlanStepView,
} from './rolloutModel';
import { rolloutMessages } from './rolloutMessages';
import { RolloutRail } from './RolloutRail';
import { RolloutSteps } from './RolloutSteps';
import { RolloutSummary } from './RolloutSummary';
import { StepSheet } from './StepSheet';
import { useMilestoneFacts } from './useMilestoneFacts';
import { useRolloutActions } from './useRolloutActions';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the run sheet; each part uses its own slice of it */
import styles from './Rollout.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface RolloutRunSheetProps {
  accountId: number;
  item: CatalogItem;
  release: GetReleaseKitResponse;
  plan: ReleasePlan;
  /** The usable channels (connected and manual). */
  channels: SerializedSocialChannel[];
  /** `YYYY-MM-DD` today in the artist's time zone. */
  today: string;
}

interface RunSheetBodyProps {
  steps: PlanStepView[];
  fit: FitResult;
  quota: { used: number; limit: number | null } | null;
  isFree: boolean;
  isOwner: boolean;
  openPlatform: string | null;
  closeStep: () => void;
}

/** The parts that read the context: the summary, the rail, the steps, "Needs you" and the sheet. */
const RunSheetBodyComponent: React.FC<RunSheetBodyProps> = ({
  steps,
  fit,
  quota,
  isFree,
  isOwner,
  openPlatform,
  closeStep,
}) => {
  const intl = useIntl();
  const { plan, today, accountId, item, openStepKey } = useRollout();
  const facts = useMilestoneFacts();
  const counts = useMemo(() => planCounts(steps), [steps]);
  const drafts = useMemo(() => planDrafts(steps), [steps]);
  const needs = useMemo(
    () =>
      planNeeds({
        steps,
        now: new Date(),
        today,
        releaseDate: plan.releaseDate,
        canvasOpen: facts.canvas !== null && !facts.canvasDone,
      }),
    [facts.canvas, facts.canvasDone, plan.releaseDate, steps, today]
  );
  const openCanvas = useCallback(() => {
    if (facts.canvas !== null) {
      openPublishDialog({
        accountId,
        kitAssetUuid: facts.canvas.uuid,
        catalogItemUuid: item.uuid,
        channel: 'spotifyCanvas',
        assetName: intl.formatMessage(rolloutMessages.canvasTitle),
        downloadUrl: facts.canvas.fileUrl === null ? null : downloadMediaUrl(facts.canvas.fileUrl),
      });
    }
  }, [accountId, facts.canvas, intl, item.uuid]);
  const quotaLine =
    quota === null || quota.limit === null || isFree
      ? null
      : { month: intl.formatDate(new Date(), { month: 'long' }), used: quota.used, limit: quota.limit };
  const openStep = steps.find((step) => step.key === openStepKey) ?? null;
  const lastDay = plan.releaseDate === null ? null : addDays(plan.releaseDate, 7);

  return (
    <div className={styles.sheet}>
      <Panel as="section" aria-label={intl.formatMessage(rolloutMessages.eyebrow)}>
        <RolloutSummary steps={steps} counts={counts} needsCount={needs.length} fit={fit}>
          <ChannelsLine quota={quotaLine} />
        </RolloutSummary>
      </Panel>
      <LaunchSummary steps={steps} />
      {/* design: limit-upsell a1, a11. It takes the place of the Free upsell line of e1. */}
      <ScheduleAllUpsell drafts={drafts} quota={quota} />
      {isFree && <FreePlanLimits fit={fit} isOwner={isOwner} showUpsell={false} />}
      <div className={styles.layout}>
        <Panel as="section">
          <RolloutRail steps={steps} facts={facts} />
          <RolloutSteps steps={steps} />
        </Panel>
        <div className={styles.side}>
          <NeedsYouPanel needs={needs} onCanvas={openCanvas} />
          {lastDay !== null && (
            <Panel as="section">
              <PanelHead
                title={intl.formatMessage(rolloutMessages.pushTitle)}
                subtitle={intl.formatMessage(rolloutMessages.pushSub)}
              />
              <PanelBody>
                <p className={styles.line}>
                  {intl.formatMessage(rolloutMessages.pushBody, { day: planDayDate(intl, lastDay) })}
                </p>
              </PanelBody>
            </Panel>
          )}
        </div>
      </div>
      <StepSheet step={openStep} platform={openPlatform} onClose={closeStep} />
      <LaunchQuestionsDialog />
    </div>
  );
};

const RunSheetBody = TypedMemo(RunSheetBodyComponent);

/**
 * The Plan tab of an upcoming release (design `docs/superpowers/design-reference/music24-rollout-plan/`,
 * frames a1, a2, b1, c1–c3, d1, e1; owner decisions 2026-10-09): a vertical run sheet of the
 * rollout. Each step shows what it posts, on which channels, at what time and in what state;
 * milestones are lines; the summary counts the progress; "Needs you" names each action one time.
 * Every member may plan (D7); the demo artist reads it only.
 */
const RolloutRunSheetComponent: React.FC<RolloutRunSheetProps> = ({
  accountId,
  item,
  release,
  plan,
  channels,
  today,
}) => {
  const isDemo = useIsDemoAccount();
  const capabilities = useMusic24Capabilities();
  const { credits } = useVideoCredits({ accountId });
  const platforms = useMemo(() => channels.map((channel) => channel.platform as SocialPlatform), [channels]);
  const actions = useRolloutActions({ accountId, catalogItemUuid: item.uuid, platforms });
  const initialStep = useQueryParam<null>('step', null, null);
  const initialChannel = useQueryParam<null>('channel', null, null);
  const [openStepKey, setOpenStepKey] = useState<string | null>(initialStep);
  const [openPlatform, setOpenPlatform] = useState<string | null>(initialChannel);
  const openStep = useCallback((key: string, platform?: string) => {
    setOpenStepKey(key);
    setOpenPlatform(platform ?? null);
  }, []);
  const closeStep = useCallback(() => setOpenStepKey(null), []);
  const steps = useMemo(() => planSteps(plan, today), [plan, today]);
  const { data: quotaAnswer } = useGetPromoQuotaQuery({ accountId });
  const quota = quotaAnswer?.scheduledPost ?? null;
  const fit = useMemo(
    () =>
      fitDrafts({
        drafts: planDrafts(steps).map((post) => ({ uuid: post.uuid, scheduledAt: post.scheduledAt })),
        limit: quota?.limit ?? null,
        used: quota?.used ?? 0,
        now: new Date(),
      }),
    [quota, steps]
  );
  const fits = useMemo(() => new Map(fit.drafts.map((draft) => [draft.postUuid, draft])), [fit]);

  const value = useMemo<RolloutContextValue>(
    () => ({
      accountId,
      item,
      release,
      plan,
      today,
      timeZone: plan.timeZone ?? null,
      channels,
      actions,
      canAct: !isDemo,
      creditsLeft: credits?.totalLeft ?? null,
      fits,
      openStep,
      openStepKey,
    }),
    [accountId, actions, channels, credits?.totalLeft, fits, isDemo, item, openStep, openStepKey, plan, release, today]
  );

  return (
    <RolloutContext.Provider value={value}>
      <RunSheetBody
        steps={steps}
        fit={fit}
        quota={quota}
        isFree={capabilities.plan === 'free'}
        isOwner={credits?.isOwner ?? true}
        openPlatform={openPlatform}
        closeStep={closeStep}
      />
    </RolloutContext.Provider>
  );
};

export const RolloutRunSheet = TypedMemo(RolloutRunSheetComponent);
